/**
 * AccountHierarchyTab — the account drilldown's Hierarchy tab, shared by both
 * shells (V1 `AccountDrillDown`, V2 `AccountLens`). Owns the data hook, the
 * tree, and the (plain `useState`) selection/expansion state; nothing here is
 * edited, so `useDraftState` does not apply. Both shells mount this body only
 * while the tab is selected, which is what keeps `/hierarchy` lazy.
 */
import { Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { lazyWithRetry } from '../../../../lib/lazyWithRetry';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Alert, AlertDescription } from '@clay-run/glaze/alert';
import { Button } from '@clay-run/glaze/button';
import { IconButton } from '@clay-run/glaze/iconbutton';
import { Icon } from '@clay-run/glaze/icon';
import { SegmentedControl, SegmentedControlOption } from '@clay-run/glaze/segmented-control';
import { Text } from '@clay-run/glaze/text';
import {
  dealFilterOptions, rollupDeals, summarizeDeals, traceIds,
} from '@shared/flow/account-hierarchy-deal-model';
import {
  buildHierarchyTree, defaultExpandedIds, filterRowsToMatching, flattenVisibleRows, withSortedChildren,
} from '@shared/flow/account-hierarchy-tree';
import { activeFilterCount, intersectLensIds, renewalLensIds, renewalLensSummary } from '@shared/flow/account-hierarchy-filters';
import { statusIdsOf, whitespaceStatusOf, type WhitespaceStatus } from '@shared/flow/account-hierarchy-whitespace';
import { FLOW_ICONS } from '../../icons';
import { Modal } from '../../../ui/Modal';
import clsx from 'clsx';
import { useAccountHierarchy } from '../hooks';
import { FOCUS_CONTACTS, FOCUS_PARAM } from '../useFocusParam';
import { withAccountTab } from '../useAccountTab';
import {
  cycleDealSort, patchDealFilters, resetAllFilters, setHealthFilter, setLensOn, setHierarchyViewMode, setLegendStatuses,
  setRenewalFilter, setShowOnlyMatching, setSidebarOpen, toggleLegendStatus, useAccountHierarchyViewStore,
  type HierarchyViewMode,
} from './account-hierarchy-view-store';
import { ColorByLegend } from './ColorByLegend';
import { HierarchySidebar } from './HierarchySidebar';
import { resolveSidebarOpen, sidebarPlacement } from './sidebarLayout';
import { useContainerWidth } from './useContainerWidth';
import { pushEscapeLayer } from '../../../ui/escape-stack';
import { LegendAccountList } from './LegendAccountList';
import { SelectedAccountCard } from './SelectedAccountCard';
import { HierarchySummary } from './HierarchySummary';
import { HIERARCHY_CANVAS_MAX_PX, HIERARCHY_CANVAS_MIN_PX, canvasHeightPx } from './moduleHeight';
import { NODE_H, NODE_H_OVERLAY, computeHierarchyLayout } from './graph/hierarchyGraphLayout';
import type { HierarchyLens } from './graph/HierarchyGraphContext';
import { HierarchyTable } from './HierarchyTable';
import type { HierarchyNodeActions } from './HierarchyNodeMenu';
import {
  DEAL_OVERLAY_COPY, HIERARCHY_COPY, LENS_COPY, dealsSummaryLabel, filtersButtonLabel, lensMatchStatus, renewalsSummaryLabel,
} from './hierarchyPresentation';

// `@xyflow/react` lives behind this dynamic import so Vite splits it out of the
// main chunk; it loads the first time a rep switches to Graph.
const HierarchyGraph = lazyWithRetry(() => import('./graph/HierarchyGraph'));

export interface AccountHierarchyTabProps {
  accountId: string;
  /** Builds a company's drilldown route — the shell owns the URL shape. */
  accountHref: (salesforceAccountId: string) => string;
}

function AccountHierarchyTabBody({ accountId, accountHref }: AccountHierarchyTabProps) {
  const navigate = useNavigate();
  const { data, isLoading, error } = useAccountHierarchy(accountId);
  // Session-scoped (survives J/K remounts; a reload resets to Graph) — see the store's doc.
  const viewMode = useAccountHierarchyViewStore(s => s.viewMode);
  const lensOn = useAccountHierarchyViewStore(s => s.lensOn);
  const dealFilters = useAccountHierarchyViewStore(s => s.dealFilters);
  const renewalFilter = useAccountHierarchyViewStore(s => s.renewalFilter);
  const healthFilter = useAccountHierarchyViewStore(s => s.healthFilter);
  const legendStatuses = useAccountHierarchyViewStore(s => s.legendStatuses);
  const showOnlyMatching = useAccountHierarchyViewStore(s => s.showOnlyMatching);
  const dealSort = useAccountHierarchyViewStore(s => s.dealSort);
  // Fiscal presets resolve against the moment the tab mounted (a keyed remount per account).
  const [now] = useState(() => new Date());

  // null = "not touched yet" -> derived defaults (the whole tree expanded),
  // so there is no seed-from-data effect to race a refetch.
  const [expandedOverride, setExpandedOverride] = useState<Set<string> | null>(null);
  const [revealedIds, setRevealedIds] = useState<Set<string>>(() => new Set());
  const [selectedOverride, setSelectedOverride] = useState<string | null>(null);
  // "Show in graph / table" (legend list): one pending frame/scroll request, dropped once handled.
  const [focusRequest, setFocusRequest] = useState<{ id: string; nonce: number } | null>(null);
  const clearFocusRequest = useCallback(() => setFocusRequest(null), []);
  // Monotonic (never reset when a request is cleared), so no two requests ever share a nonce.
  const focusNonceRef = useRef(0);

  // Full screen: ONE instance of the body. Its output is portaled into a persistent `host` element
  // that is moved (not remounted) between the inline slot and the full-screen overlay, so
  // selection, expansion, filters, view mode and the React Flow instance all survive the toggle,
  // and only one graph is ever mounted.
  const [fullscreen, setFullscreen] = useState(false);
  // Filters sidebar: open/closed follows the rep's last choice per view, else the container width
  // (wide or full screen: open and docked beside the canvas; narrow: collapsed, opening as a side sheet).
  const sidebarChoice = useAccountHierarchyViewStore(s => s.sidebarChoice);
  const [containerWidth, containerRef] = useContainerWidth();
  const sidebarCtx = { fullscreen, containerWidth };
  const sidebarOpen = resolveSidebarOpen(sidebarChoice, sidebarCtx);
  const placement = sidebarPlacement(sidebarCtx);
  const sidebarView = fullscreen ? 'fullscreen' : 'inline';
  const sheetOpen = sidebarOpen && placement === 'sheet';
  // The side sheet is a layer: Escape closes only the sheet (an open popover's own guard layer sits above it).
  useEffect(() => {
    if (!sheetOpen) return undefined;
    return pushEscapeLayer(() => setSidebarOpen('inline', false));
  }, [sheetOpen]);
  // Stable identity: `ui/Modal` re-registers its Escape layer whenever `onClose` changes, which would
  // put it above an open menu's guard layer and let Escape close the dialog under the menu.
  const closeFullscreen = useCallback(() => setFullscreen(false), []);
  const [host] = useState(() => document.createElement('div'));
  const slotRef = useRef<HTMLDivElement | null>(null);
  const overlayBodyRef = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    const target = fullscreen ? overlayBodyRef.current : slotRef.current;
    host.className = fullscreen ? 'h-full min-h-0' : 'contents';
    if (target && host.parentElement !== target) {
      // Moving a DOM node blurs whatever was focused inside it (the Expand button the rep just
      // pressed): put focus back so the modal trap records it as the element to restore on close.
      const focused = document.activeElement;
      target.appendChild(host);
      if (focused instanceof HTMLElement && host.contains(focused)) focused.focus();
    }
  });
  // The overlay is the shared `ui/Modal`: it owns the focus trap + restore, scroll lock and Escape
  // (topmost layer only, so an open menu's `useMenuEscapeGuard` layer takes the keypress first).

  const tree = useMemo(() => (data ? buildHierarchyTree(data.nodes, data.root_id) : null), [data]);
  const expandedIds = useMemo(
    () => expandedOverride ?? (tree && data ? defaultExpandedIds(tree, data.current_id) : new Set<string>()),
    [expandedOverride, tree, data],
  );
  // Deals lens: everything derives from the response's buckets + the filter state, so a
  // filter change never refetches. Summaries and roll-ups only exist while the Deals lens is active.
  const dealOverlayEnabled = lensOn.deals;
  const dealBuckets = data?.deal_buckets;
  const dealSummaries = useMemo(
    () => (dealOverlayEnabled && dealBuckets ? summarizeDeals(dealBuckets, dealFilters, now) : null),
    [dealOverlayEnabled, dealBuckets, dealFilters, now],
  );
  const dealRollups = useMemo(
    () => (tree && dealSummaries ? rollupDeals(tree, dealSummaries) : null),
    [tree, dealSummaries],
  );
  const filterOptions = useMemo(() => dealFilterOptions(dealBuckets ?? []), [dealBuckets]);
  // Trace (ancestors + self + descendants) only follows an explicit pick; the default selection
  // is the current account and would otherwise dim the whole tree on open.
  const trace = useMemo(
    () => (dealSummaries && tree && selectedOverride !== null ? traceIds(tree, selectedOverride) : null),
    [dealSummaries, tree, selectedOverride],
  );
  const overlay = useMemo(
    () => (dealSummaries && dealRollups ? { summaries: dealSummaries, rollups: dealRollups, filters: dealFilters, now, traceIds: trace } : null),
    [dealSummaries, dealRollups, dealFilters, now, trace],
  );

  // Customer status always drives card TONE (and its legend picks what to highlight); every lens
  // produces an id set: the highlighted accounts (null = nothing dims). Dimming is opacity-only,
  // so it never fights the tone.
  const statusCounts = useMemo(() => {
    const counts: Record<WhitespaceStatus, number> = { customer: 0, open_pipeline: 0, churned: 0, prospect_contacts: 0, untouched: 0 };
    for (const n of data?.nodes ?? []) counts[whitespaceStatusOf(n)] += 1;
    return counts;
  }, [data]);
  const legendIds = useMemo(
    () => (data ? statusIdsOf(data.nodes, legendStatuses) : null),
    [data, legendStatuses],
  );
  const renewalIds = useMemo(
    () => (data && lensOn.renewals ? renewalLensIds(data.nodes, renewalFilter, healthFilter, now) : null),
    [data, lensOn.renewals, renewalFilter, healthFilter, now],
  );
  const dealIds = useMemo(() => (dealSummaries ? new Set(dealSummaries.keys()) : null), [dealSummaries]);
  const highlightedIds = useMemo(() => intersectLensIds([dealIds, renewalIds, legendIds]), [dealIds, renewalIds, legendIds]);
  // The canvas autofits the tree (a minimum, growing with the laid-out tree; the shell lets the module grow).
  const canvasPx = useMemo(
    () => (tree && data
      ? canvasHeightPx(computeHierarchyLayout({ tree, expandedIds, revealedIds, currentId: data.current_id, nodeHeight: dealOverlayEnabled ? NODE_H_OVERLAY : NODE_H }).height)
      : HIERARCHY_CANVAS_MIN_PX),
    [tree, data, expandedIds, revealedIds, dealOverlayEnabled],
  );
  // Table: siblings sort by a Deal column (tree grouping kept); accounts with no matching deal
  // always sort last. "Show only matching" then drops rows no lens highlights.
  const tableTree = useMemo(() => {
    if (!tree || !dealSort || !dealSummaries) return tree;
    const value = (n: { id: string }): number | null => {
      const summary = dealSummaries.get(n.id);
      return summary ? (dealSort.key === 'dealAmount' ? summary.amount : summary.arr) : null;
    };
    const sign = dealSort.dir === 'desc' ? -1 : 1;
    return withSortedChildren(tree, (a, b) => {
      const va = value(a);
      const vb = value(b);
      if (va === null || vb === null) return va === vb ? 0 : va === null ? 1 : -1;
      return sign * (va - vb);
    });
  }, [tree, dealSort, dealSummaries]);
  const rows = useMemo(() => {
    if (!tableTree || !data) return [];
    // With Show only matching, the overflow cap must not hide a match's path: reveal every parent
    // (the filter then drops everything that is not a match or an ancestor of one).
    const revealed = showOnlyMatching && highlightedIds ? new Set(tableTree.childrenOf.keys()) : revealedIds;
    const all = flattenVisibleRows(tableTree, expandedIds, revealed, data.current_id);
    return showOnlyMatching ? filterRowsToMatching(all, highlightedIds, tableTree) : all;
  }, [tableTree, data, expandedIds, revealedIds, showOnlyMatching, highlightedIds]);
  const legendAccounts = useMemo(
    () => (data && legendIds ? data.nodes.filter(n => legendIds.has(n.id)).sort((a, b) => (a.name ?? a.id).localeCompare(b.name ?? b.id)) : []),
    [data, legendIds],
  );
  // Every account that has ANY deal (any status, any filter): the menu's "View deals".
  const dealAccountIds = useMemo(() => new Set((dealBuckets ?? []).map(b => b.account_id)), [dealBuckets]);
  const lens = useMemo<HierarchyLens>(() => ({ highlightedIds, today: now }), [highlightedIds, now]);
  const renewalSummary = useMemo(
    () => (data && renewalIds ? renewalLensSummary(data.nodes, renewalIds) : null),
    [data, renewalIds],
  );

  if (isLoading) return <Text size="xs" variant="tertiary">{HIERARCHY_COPY.loading}</Text>;
  if (error || !data || !tree) return <Text size="xs" variant="critical">{HIERARCHY_COPY.error}</Text>;
  // A response whose root row is absent cannot be drawn (the server never sends one; guard anyway).
  if (!tree.byId.has(data.root_id)) return <Text size="xs" variant="critical">{HIERARCHY_COPY.error}</Text>;

  const onlyNode = data.nodes.length === 1 ? data.nodes[0] : null;
  if (data.nodes.length === 0 || (onlyNode && !onlyNode.parent_missing)) {
    return <Text size="xs" variant="tertiary">{HIERARCHY_COPY.empty}</Text>;
  }

  const currentId = data.current_id;
  const selectedId = selectedOverride ?? currentId;
  const selectedNode = tree.byId.get(selectedId) ?? null;
  const rootDeals = dealRollups?.get(tree.rootId);
  const rootDealTotal = rootDeals && rootDeals.count > 0 ? rootDeals : null;
  const summaryLines: string[] = [];
  if (overlay) summaryLines.push(dealsSummaryLabel(overlay.summaries.size, rootDealTotal));
  if (renewalSummary) summaryLines.push(renewalsSummaryLabel(renewalSummary.accounts, renewalSummary.arrAtRisk));

  const toggleExpanded = (id: string) => {
    setExpandedOverride(() => {
      const next = new Set(expandedIds);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const revealChildren = (parentId: string) => setRevealedIds(prev => new Set(prev).add(parentId));

  // Legend list: select the account, make sure it is not hidden (collapsed ancestors, "+N accounts"
  // overflow), switch to the requested view and ask it to frame / scroll to the node.
  const showAccount = (id: string, view: HierarchyViewMode) => {
    const ancestors = tree.ancestorsOf(id);
    setExpandedOverride(() => new Set([...expandedIds, ...ancestors]));
    // Every ancestor on the path lifts its own ">12 children" cap, not only the target's parent.
    setRevealedIds(prev => new Set([...prev, ...ancestors]));
    setSelectedOverride(id);
    setHierarchyViewMode(view);
    focusNonceRef.current += 1;
    setFocusRequest({ id, nonce: focusNonceRef.current });
  };

  const actions: HierarchyNodeActions = {
    onOpenAccount: id => navigate(accountHref(id)),
    onViewContacts: id => navigate(`${withAccountTab(accountHref(id), 'people')}&${FOCUS_PARAM}=${FOCUS_CONTACTS}`),
    onViewDeals: id => navigate(withAccountTab(accountHref(id), 'opps')),
    onToggleChildren: id => {
      // Expanding from the menu also lifts the >12-children cap ("Expand children" reveals).
      if (!expandedIds.has(id)) revealChildren(id);
      toggleExpanded(id);
    },
  };

  const title = tree.byId.get(currentId)?.name ?? currentId;
  const activeCount = activeFilterCount(lensOn, { dealFilters, renewalFilter, healthFilter, legendStatuses });
  const sidebar = (extra: { onClose?: () => void; className?: string }) => (
    <HierarchySidebar
      lensOn={lensOn}
      onLensToggle={setLensOn}
      activeCount={activeCount}
      onResetAll={resetAllFilters}
      deals={{ filters: dealFilters, onChange: patchDealFilters, options: filterOptions, now }}
      renewals={{ renewal: renewalFilter, onRenewalChange: setRenewalFilter, health: healthFilter, onHealthChange: setHealthFilter }}
      customerStatus={(
        <div className="flex flex-col gap-2" data-testid="customer-status-lens">
          <ColorByLegend counts={statusCounts} selected={legendStatuses} onToggle={toggleLegendStatus} onClear={() => setLegendStatuses([])} />
          {legendIds && legendAccounts.length > 0 && (
            <LegendAccountList
              accounts={legendAccounts}
              accountHref={accountHref}
              onShowInGraph={id => showAccount(id, 'graph')}
              onShowInTable={id => showAccount(id, 'table')}
            />
          )}
        </div>
      )}
      table={viewMode === 'table' ? {
        showOnlyMatching,
        onShowOnlyMatchingChange: setShowOnlyMatching,
        matching: highlightedIds ? data.nodes.filter(n => highlightedIds.has(n.id)).length : data.nodes.length,
        total: data.nodes.length,
      } : null}
      footer={fullscreen && selectedNode ? (
        <SelectedAccountCard
          node={selectedNode}
          facts={{
            accountId: selectedNode.id,
            isCurrent: selectedNode.id === currentId,
            hasChildren: (tree.childrenOf.get(selectedNode.id)?.length ?? 0) > 0,
            hasDeals: dealAccountIds.has(selectedNode.id),
            childrenExpanded: expandedIds.has(selectedNode.id),
            actions,
          }}
        />
      ) : null}
      {...extra}
    />
  );

  const content = (
    <div ref={containerRef} className={clsx('relative flex flex-col gap-2', fullscreen && 'h-full min-h-0')} data-testid="account-hierarchy-tab">
      {/* Keyboard order: this header row, then the canvas / table, then the Filters sidebar. */}
      <div className="flex items-center justify-end gap-2">
        <SegmentedControl
          aria-label="Hierarchy view"
          size="xs"
          value={viewMode}
          onValueChange={(next: HierarchyViewMode) => setHierarchyViewMode(next)}
        >
          <SegmentedControlOption value="table"><Icon type={FLOW_ICONS.tableView} size="xs" aria-hidden /> Table</SegmentedControlOption>
          <SegmentedControlOption value="graph"><Icon type={FLOW_ICONS.graphView} size="xs" aria-hidden /> Graph</SegmentedControlOption>
        </SegmentedControl>
        <Button
          type="button"
          size="sm"
          variant={sidebarOpen ? 'secondary' : 'ghost'}
          aria-expanded={sidebarOpen}
          aria-controls="hierarchy-filters-sidebar"
          onClick={() => setSidebarOpen(sidebarView, !sidebarOpen)}
        >
          {filtersButtonLabel(activeCount)}
        </Button>
        <IconButton
          variant="ghost"
          size="sm"
          icon={fullscreen ? FLOW_ICONS.fullscreenExit : FLOW_ICONS.fullscreen}
          aria-label={fullscreen ? 'Exit full screen' : 'Expand to full screen'}
          onClick={() => setFullscreen(open => !open)}
        />
      </div>
      <div className={clsx('flex gap-3', fullscreen && 'min-h-0 flex-1')}>
      <div className="flex min-w-0 flex-1 flex-col gap-2">
      {highlightedIds && (
        <span role="status" className="sr-only">{lensMatchStatus(highlightedIds.size, data.nodes.length)}</span>
      )}
      {renewalIds && renewalIds.size === 0 && (
        <Text size="xs" variant="tertiary">{LENS_COPY.noRenewals}</Text>
      )}
      {overlay && overlay.summaries.size === 0 && (
        <Text size="xs" variant="tertiary">{data.deals_truncated ? DEAL_OVERLAY_COPY.emptyMatchTruncated : DEAL_OVERLAY_COPY.emptyMatch}</Text>
      )}
      {overlay && data.deals_truncated && (
        <Alert status="warning">
          <AlertDescription>{DEAL_OVERLAY_COPY.truncated}</AlertDescription>
        </Alert>
      )}
      {data.truncated && (
        <Alert status="warning">
          <AlertDescription>{HIERARCHY_COPY.truncated}</AlertDescription>
        </Alert>
      )}
      {viewMode === 'graph' ? (
        <div
          role="region"
          aria-label="Account hierarchy graph"
          className={clsx('relative w-full overflow-hidden rounded-lg border border-border-primary', fullscreen && 'min-h-0 flex-1')}
          style={fullscreen ? undefined : { height: canvasPx, minHeight: HIERARCHY_CANVAS_MIN_PX, maxHeight: HIERARCHY_CANVAS_MAX_PX }}
          data-testid="hierarchy-graph-container"
        >
          <HierarchySummary lines={summaryLines} variant="pill" />
          <Suspense fallback={<Text size="xs" variant="tertiary" className="p-3">{HIERARCHY_COPY.loadingGraph}</Text>}>
            <HierarchyGraph
              tree={tree}
              expandedIds={expandedIds}
              revealedIds={revealedIds}
              currentId={currentId}
              selectedId={selectedId}
              actions={actions}
              onSelect={setSelectedOverride}
              onReveal={revealChildren}
              overlay={overlay}
              lens={lens}
              dealAccountIds={dealAccountIds}
              focusRequest={focusRequest}
              onFocusHandled={clearFocusRequest}
              frameKey={fullscreen ? 'fullscreen' : 'inline'}
            />
          </Suspense>
        </div>
      ) : (
        <>
        <HierarchySummary lines={summaryLines} variant="line" />
        {/* Same capped box as the graph canvas: the table scrolls inside it (full screen: fills the dialog). */}
        <div
          data-testid="hierarchy-table-container"
          className={clsx('w-full overflow-auto rounded-lg border border-border-primary', fullscreen && 'min-h-0 flex-1')}
          style={fullscreen ? undefined : { maxHeight: HIERARCHY_CANVAS_MAX_PX }}
        >
        <HierarchyTable
          rows={rows}
          currentId={currentId}
          selectedId={selectedId}
          actions={actions}
          onSelect={setSelectedOverride}
          onToggle={toggleExpanded}
          onReveal={revealChildren}
          dealSummaries={dealSummaries}
          lens={lens}
          dealAccountIds={dealAccountIds}
          sort={dealSort}
          onSortChange={cycleDealSort}
          focusRequest={focusRequest}
          onFocusHandled={clearFocusRequest}
        />
        </div>
        </>
      )}
      </div>
      {sidebarOpen && placement === 'docked' && <div id="hierarchy-filters-sidebar" className={clsx(fullscreen && 'min-h-0')}>{sidebar({ className: fullscreen ? 'h-full' : 'max-h-[580px]' })}</div>}
      </div>
      {sheetOpen && (
        <div id="hierarchy-filters-sidebar" className="absolute inset-y-0 right-0 z-20 flex shadow-lg">
          {sidebar({ onClose: () => setSidebarOpen('inline', false), className: 'h-full' })}
        </div>
      )}
    </div>
  );

  return (
    <>
      <div ref={slotRef}>
        {fullscreen && <Text size="xs" variant="tertiary">The hierarchy is open full screen.</Text>}
      </div>
      <Modal
        open={fullscreen}
        title={title}
        onClose={closeFullscreen}
        ariaLabel={`Account hierarchy: ${title}`}
        closeOnBackdrop={false}
        containerClassName="md:!h-full md:!max-h-none md:!w-full md:!max-w-none md:!rounded-none md:!border-0"
        bodyClassName="!p-4 flex min-h-0 flex-col"
      >
        <div ref={overlayBodyRef} className="min-h-0 flex-1" data-testid="hierarchy-fullscreen" />
      </Modal>
      {createPortal(content, host)}
    </>
  );
}

/**
 * Per-account state (expansion, reveals, selection) resets via
 * the React `key`, not an effect: both shells keep this mounted across J/K
 * account steps, and a keyed remount is StrictMode-safe by construction.
 */
export function AccountHierarchyTab(props: AccountHierarchyTabProps) {
  return <AccountHierarchyTabBody key={props.accountId} {...props} />;
}
